/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Verdi Dashboard API
 * API behind the Verdi irrigation dashboard: organizations, their devices, zones and irrigation schedules, and the sensor data those devices report.
 *
 * ## Authentication
 *
 * Most operations need a Descope session token, sent as `Authorization: Bearer <token>`. Each operation's `security` lists what it accepts. Role and permission names there, such as `Verdi Administrator`, are Descope roles or permissions the session must carry, and the operation's description repeats them under **Access**. Operations with `security: []` are public.
 *
 * ## Organization context
 *
 * Data belongs to organizations. A user's session acts as their primary organization, or as another organization they belong to when its ID is sent in the `User` header. Administrators must send the `User` header on organization-scoped operations, or the request is rejected with a 400.
 *
 * ## Errors
 *
 * Error bodies are JSON `{ "message": string }`.
 *
 * - **400**: the path, query or body failed validation, or the request broke a domain rule.
 * - **401 / 403**: sent by the auth middleware; the body may be empty.
 * - **409**: see below.
 * - **500**: an unexpected server error.
 *
 * ## Versioned resources
 *
 * Versioned resources carry a `versionId`. Send the `versionId` you last read on a PATCH (in the body) or a DELETE (as a query parameter). If the resource changed since, the request gets a 409: reload it and retry. A 409 that includes a `code` is a conflict specific to that operation, described on the operation. On a create, 409 means the resource already exists.
 *
 * ## Reads with a request body
 *
 * Some reads take their input as a JSON body on the HTTP `QUERY` method; these operations are marked `x-read-only: true`. Deprecated POST and GET variants serve clients that cannot send `QUERY`.
 *
 * ## Identifiers
 *
 * MongoDB ObjectIds are 24-character hexadecimal strings.
 * OpenAPI spec version: 1.0.0
 */

import type {
	DataTag,
	DefinedInitialDataOptions,
	DefinedUseQueryResult,
	MutationFunction,
	QueryClient,
	QueryFunction,
	QueryKey,
	UndefinedInitialDataOptions,
	UseMutationOptions,
	UseMutationResult,
	UseQueryOptions,
	UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "../custom-fetch.ts";
import type {
	CreateSensoterraDevice400,
	CreateSensoterraDevice403,
	CreateSensoterraDevice500,
	CreateSensoterraDevice502,
	DeleteSensoterraDevice400,
	DeleteSensoterraDevice404,
	DeleteSensoterraDevice500,
	GetSensoterraDevice400,
	GetSensoterraDevice404,
	GetSensoterraDevice500,
	ListSensoterraDevices400,
	ListSensoterraDevices500,
	SensoterraDeviceUpdate,
	Sensoterradevices,
	SensoterradevicesCreate,
	UpdateSensoterraDevice400,
	UpdateSensoterraDevice404,
	UpdateSensoterraDevice500,
} from "./schemas";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
	query: T,
	queryKey: K,
): T & { queryKey: K } => {
	const result = { queryKey } as T & { queryKey: K };
	for (const key of Object.keys(query)) {
		// The explicit queryKey always wins, matching the previous
		// `{ ...query, queryKey }` spread where it was set last.
		if (key === "queryKey") continue;
		Object.defineProperty(result, key, {
			enumerable: true,
			configurable: true,
			get: () => (query as Record<string, unknown>)[key],
		});
	}
	return result;
};

export const getListSensoterraDevicesUrl = () => {
	return `/sensoterra-devices`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary List all Sensoterra devices owned by the authenticated user
 */
export const listSensoterraDevices = async (
	options?: Parameters<typeof customFetch>[1],
): Promise<Sensoterradevices[]> => {
	return customFetch<Sensoterradevices[]>(getListSensoterraDevicesUrl(), {
		...options,
		method: "GET",
	});
};

export const getListSensoterraDevicesQueryKey = () => {
	return ["sensoterra-devices"] as const;
};

export const getListSensoterraDevicesQueryOptions = <
	TData = Awaited<ReturnType<typeof listSensoterraDevices>>,
	TError = ListSensoterraDevices400 | void | ListSensoterraDevices500,
>(options?: {
	query?: Partial<
		UseQueryOptions<
			Awaited<ReturnType<typeof listSensoterraDevices>>,
			TError,
			TData
		>
	>;
	request?: SecondParameter<typeof customFetch>;
}) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getListSensoterraDevicesQueryKey();

	const queryFn: QueryFunction<
		Awaited<ReturnType<typeof listSensoterraDevices>>
	> = ({ signal }) => listSensoterraDevices({ signal, ...requestOptions });

	return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
		Awaited<ReturnType<typeof listSensoterraDevices>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSensoterraDevicesQueryResult = NonNullable<
	Awaited<ReturnType<typeof listSensoterraDevices>>
>;
export type ListSensoterraDevicesQueryError =
	| ListSensoterraDevices400
	| void
	| ListSensoterraDevices500;

export function useListSensoterraDevices<
	TData = Awaited<ReturnType<typeof listSensoterraDevices>>,
	TError = ListSensoterraDevices400 | void | ListSensoterraDevices500,
>(
	options: {
		query: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listSensoterraDevices>>,
				TError,
				TData
			>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof listSensoterraDevices>>,
					TError,
					Awaited<ReturnType<typeof listSensoterraDevices>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListSensoterraDevices<
	TData = Awaited<ReturnType<typeof listSensoterraDevices>>,
	TError = ListSensoterraDevices400 | void | ListSensoterraDevices500,
>(
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listSensoterraDevices>>,
				TError,
				TData
			>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof listSensoterraDevices>>,
					TError,
					Awaited<ReturnType<typeof listSensoterraDevices>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListSensoterraDevices<
	TData = Awaited<ReturnType<typeof listSensoterraDevices>>,
	TError = ListSensoterraDevices400 | void | ListSensoterraDevices500,
>(
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listSensoterraDevices>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List all Sensoterra devices owned by the authenticated user
 */

export function useListSensoterraDevices<
	TData = Awaited<ReturnType<typeof listSensoterraDevices>>,
	TError = ListSensoterraDevices400 | void | ListSensoterraDevices500,
>(
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listSensoterraDevices>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getListSensoterraDevicesQueryOptions(options);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List all Sensoterra devices owned by the authenticated user
 */
export const useSetListSensoterraDevicesQueryData = () => {
	const queryClient = useQueryClient();
	return (
		updater:
			| Awaited<ReturnType<typeof listSensoterraDevices>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof listSensoterraDevices>> | undefined,
			  ) => Awaited<ReturnType<typeof listSensoterraDevices>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<
			Awaited<ReturnType<typeof listSensoterraDevices>>
		>(
			{ exact: $exactMatch, queryKey: getListSensoterraDevicesQueryKey() },
			updater,
		);
	};
};

/**
 * @summary List all Sensoterra devices owned by the authenticated user
 */
export const useGetListSensoterraDevicesQueryData = () => {
	const queryClient = useQueryClient();
	return () =>
		queryClient.getQueryData<Awaited<ReturnType<typeof listSensoterraDevices>>>(
			getListSensoterraDevicesQueryKey(),
		);
};

export const getCreateSensoterraDeviceUrl = () => {
	return `/sensoterra-devices`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary Register a new Sensoterra device
 */
export const createSensoterraDevice = async (
	sensoterradevicesCreate: SensoterradevicesCreate,
	options?: Parameters<typeof customFetch>[1],
): Promise<Sensoterradevices> => {
	const getHeaders = (
		h?: NonNullable<RequestInit["headers"]>,
	): Record<string, string | readonly string[]> => {
		if (!h) return {};
		if (h instanceof Headers) return Object.fromEntries(h.entries());
		if (Symbol.iterator in h) {
			return Object.fromEntries(
				Array.from(
					h as Iterable<Iterable<string>>,
					(entry) => Array.from(entry) as [string, string],
				),
			);
		}
		const headers: Record<string, string | readonly string[]> = {};
		for (const [name, value] of Object.entries<
			string | readonly string[] | undefined
		>(h)) {
			if (value !== undefined) headers[name] = value;
		}
		return headers;
	};
	return customFetch<Sensoterradevices>(getCreateSensoterraDeviceUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...getHeaders(options?.headers),
		},
		body: JSON.stringify(sensoterradevicesCreate),
	});
};

export const getCreateSensoterraDeviceMutationKey = () =>
	["createSensoterraDevice"] as const;

export const getCreateSensoterraDeviceMutationOptions = <
	TError =
		| CreateSensoterraDevice400
		| void
		| CreateSensoterraDevice403
		| CreateSensoterraDevice500
		| CreateSensoterraDevice502,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof createSensoterraDevice>>,
		TError,
		CreateSensoterraDeviceMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof createSensoterraDevice>>,
	TError,
	CreateSensoterraDeviceMutationVariables,
	TContext
> => {
	const mutationKey = getCreateSensoterraDeviceMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof createSensoterraDevice>>,
		CreateSensoterraDeviceMutationVariables
	> = (props) => {
		const { data } = props ?? {};

		return createSensoterraDevice(data, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type CreateSensoterraDeviceMutationResult = NonNullable<
	Awaited<ReturnType<typeof createSensoterraDevice>>
>;
export type CreateSensoterraDeviceMutationBody = SensoterradevicesCreate;
export type CreateSensoterraDeviceMutationError =
	| CreateSensoterraDevice400
	| void
	| CreateSensoterraDevice403
	| CreateSensoterraDevice500
	| CreateSensoterraDevice502;
export type CreateSensoterraDeviceMutationVariables = {
	data: SensoterradevicesCreate;
};

/**
 * @summary Register a new Sensoterra device
 */
export const useCreateSensoterraDevice = <
	TError =
		| CreateSensoterraDevice400
		| void
		| CreateSensoterraDevice403
		| CreateSensoterraDevice500
		| CreateSensoterraDevice502,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof createSensoterraDevice>>,
			TError,
			CreateSensoterraDeviceMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof createSensoterraDevice>>,
	TError,
	CreateSensoterraDeviceMutationVariables,
	TContext
> => {
	return useMutation(
		getCreateSensoterraDeviceMutationOptions(options),
		queryClient,
	);
};
export const getGetSensoterraDeviceUrl = (sensoterraDeviceId: string) => {
	return `/sensoterra-devices/${sensoterraDeviceId}`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary Get a single Sensoterra device by ID
 */
export const getSensoterraDevice = async (
	sensoterraDeviceId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<Sensoterradevices> => {
	return customFetch<Sensoterradevices>(
		getGetSensoterraDeviceUrl(sensoterraDeviceId),
		{
			...options,
			method: "GET",
		},
	);
};

export const getGetSensoterraDeviceQueryKey = (sensoterraDeviceId: string) => {
	return ["sensoterra-devices", sensoterraDeviceId] as const;
};

export const getGetSensoterraDeviceQueryOptions = <
	TData = Awaited<ReturnType<typeof getSensoterraDevice>>,
	TError =
		| GetSensoterraDevice400
		| void
		| GetSensoterraDevice404
		| GetSensoterraDevice500,
>(
	sensoterraDeviceId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getSensoterraDevice>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey =
		queryOptions?.queryKey ??
		getGetSensoterraDeviceQueryKey(sensoterraDeviceId);

	const queryFn: QueryFunction<
		Awaited<ReturnType<typeof getSensoterraDevice>>
	> = ({ signal }) =>
		getSensoterraDevice(sensoterraDeviceId, { signal, ...requestOptions });

	return {
		queryKey,
		queryFn,
		enabled: sensoterraDeviceId !== null && sensoterraDeviceId !== undefined,
		...queryOptions,
	} as UseQueryOptions<
		Awaited<ReturnType<typeof getSensoterraDevice>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSensoterraDeviceQueryResult = NonNullable<
	Awaited<ReturnType<typeof getSensoterraDevice>>
>;
export type GetSensoterraDeviceQueryError =
	| GetSensoterraDevice400
	| void
	| GetSensoterraDevice404
	| GetSensoterraDevice500;

export function useGetSensoterraDevice<
	TData = Awaited<ReturnType<typeof getSensoterraDevice>>,
	TError =
		| GetSensoterraDevice400
		| void
		| GetSensoterraDevice404
		| GetSensoterraDevice500,
>(
	sensoterraDeviceId: string,
	options: {
		query: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getSensoterraDevice>>,
				TError,
				TData
			>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof getSensoterraDevice>>,
					TError,
					Awaited<ReturnType<typeof getSensoterraDevice>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetSensoterraDevice<
	TData = Awaited<ReturnType<typeof getSensoterraDevice>>,
	TError =
		| GetSensoterraDevice400
		| void
		| GetSensoterraDevice404
		| GetSensoterraDevice500,
>(
	sensoterraDeviceId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getSensoterraDevice>>,
				TError,
				TData
			>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof getSensoterraDevice>>,
					TError,
					Awaited<ReturnType<typeof getSensoterraDevice>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetSensoterraDevice<
	TData = Awaited<ReturnType<typeof getSensoterraDevice>>,
	TError =
		| GetSensoterraDevice400
		| void
		| GetSensoterraDevice404
		| GetSensoterraDevice500,
>(
	sensoterraDeviceId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getSensoterraDevice>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get a single Sensoterra device by ID
 */

export function useGetSensoterraDevice<
	TData = Awaited<ReturnType<typeof getSensoterraDevice>>,
	TError =
		| GetSensoterraDevice400
		| void
		| GetSensoterraDevice404
		| GetSensoterraDevice500,
>(
	sensoterraDeviceId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getSensoterraDevice>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getGetSensoterraDeviceQueryOptions(
		sensoterraDeviceId,
		options,
	);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get a single Sensoterra device by ID
 */
export const useSetGetSensoterraDeviceQueryData = () => {
	const queryClient = useQueryClient();
	return (
		sensoterraDeviceId: string,
		updater:
			| Awaited<ReturnType<typeof getSensoterraDevice>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof getSensoterraDevice>> | undefined,
			  ) => Awaited<ReturnType<typeof getSensoterraDevice>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<Awaited<ReturnType<typeof getSensoterraDevice>>>(
			{
				exact: $exactMatch,
				queryKey: getGetSensoterraDeviceQueryKey(sensoterraDeviceId),
			},
			updater,
		);
	};
};

/**
 * @summary Get a single Sensoterra device by ID
 */
export const useGetGetSensoterraDeviceQueryData = () => {
	const queryClient = useQueryClient();
	return (sensoterraDeviceId: string) =>
		queryClient.getQueryData<Awaited<ReturnType<typeof getSensoterraDevice>>>(
			getGetSensoterraDeviceQueryKey(sensoterraDeviceId),
		);
};

export const getUpdateSensoterraDeviceUrl = (sensoterraDeviceId: string) => {
	return `/sensoterra-devices/${sensoterraDeviceId}`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary Update an existing Sensoterra device
 */
export const updateSensoterraDevice = async (
	sensoterraDeviceId: string,
	sensoterraDeviceUpdate: SensoterraDeviceUpdate,
	options?: Parameters<typeof customFetch>[1],
): Promise<Sensoterradevices> => {
	const getHeaders = (
		h?: NonNullable<RequestInit["headers"]>,
	): Record<string, string | readonly string[]> => {
		if (!h) return {};
		if (h instanceof Headers) return Object.fromEntries(h.entries());
		if (Symbol.iterator in h) {
			return Object.fromEntries(
				Array.from(
					h as Iterable<Iterable<string>>,
					(entry) => Array.from(entry) as [string, string],
				),
			);
		}
		const headers: Record<string, string | readonly string[]> = {};
		for (const [name, value] of Object.entries<
			string | readonly string[] | undefined
		>(h)) {
			if (value !== undefined) headers[name] = value;
		}
		return headers;
	};
	return customFetch<Sensoterradevices>(
		getUpdateSensoterraDeviceUrl(sensoterraDeviceId),
		{
			...options,
			method: "PATCH",
			headers: {
				"Content-Type": "application/json",
				...getHeaders(options?.headers),
			},
			body: JSON.stringify(sensoterraDeviceUpdate),
		},
	);
};

export const getUpdateSensoterraDeviceMutationKey = () =>
	["updateSensoterraDevice"] as const;

export const getUpdateSensoterraDeviceMutationOptions = <
	TError =
		| UpdateSensoterraDevice400
		| void
		| UpdateSensoterraDevice404
		| UpdateSensoterraDevice500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof updateSensoterraDevice>>,
		TError,
		UpdateSensoterraDeviceMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof updateSensoterraDevice>>,
	TError,
	UpdateSensoterraDeviceMutationVariables,
	TContext
> => {
	const mutationKey = getUpdateSensoterraDeviceMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof updateSensoterraDevice>>,
		UpdateSensoterraDeviceMutationVariables
	> = (props) => {
		const { sensoterraDeviceId, data } = props ?? {};

		return updateSensoterraDevice(sensoterraDeviceId, data, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type UpdateSensoterraDeviceMutationResult = NonNullable<
	Awaited<ReturnType<typeof updateSensoterraDevice>>
>;
export type UpdateSensoterraDeviceMutationBody = SensoterraDeviceUpdate;
export type UpdateSensoterraDeviceMutationError =
	| UpdateSensoterraDevice400
	| void
	| UpdateSensoterraDevice404
	| UpdateSensoterraDevice500;
export type UpdateSensoterraDeviceMutationVariables = {
	sensoterraDeviceId: string;
	data: SensoterraDeviceUpdate;
};

/**
 * @summary Update an existing Sensoterra device
 */
export const useUpdateSensoterraDevice = <
	TError =
		| UpdateSensoterraDevice400
		| void
		| UpdateSensoterraDevice404
		| UpdateSensoterraDevice500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof updateSensoterraDevice>>,
			TError,
			UpdateSensoterraDeviceMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof updateSensoterraDevice>>,
	TError,
	UpdateSensoterraDeviceMutationVariables,
	TContext
> => {
	return useMutation(
		getUpdateSensoterraDeviceMutationOptions(options),
		queryClient,
	);
};
export const getDeleteSensoterraDeviceUrl = (sensoterraDeviceId: string) => {
	return `/sensoterra-devices/${sensoterraDeviceId}`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary Delete a Sensoterra device
 */
export const deleteSensoterraDevice = async (
	sensoterraDeviceId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<Sensoterradevices> => {
	return customFetch<Sensoterradevices>(
		getDeleteSensoterraDeviceUrl(sensoterraDeviceId),
		{
			...options,
			method: "DELETE",
		},
	);
};

export const getDeleteSensoterraDeviceMutationKey = () =>
	["deleteSensoterraDevice"] as const;

export const getDeleteSensoterraDeviceMutationOptions = <
	TError =
		| DeleteSensoterraDevice400
		| void
		| DeleteSensoterraDevice404
		| DeleteSensoterraDevice500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof deleteSensoterraDevice>>,
		TError,
		DeleteSensoterraDeviceMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof deleteSensoterraDevice>>,
	TError,
	DeleteSensoterraDeviceMutationVariables,
	TContext
> => {
	const mutationKey = getDeleteSensoterraDeviceMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof deleteSensoterraDevice>>,
		DeleteSensoterraDeviceMutationVariables
	> = (props) => {
		const { sensoterraDeviceId } = props ?? {};

		return deleteSensoterraDevice(sensoterraDeviceId, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type DeleteSensoterraDeviceMutationResult = NonNullable<
	Awaited<ReturnType<typeof deleteSensoterraDevice>>
>;

export type DeleteSensoterraDeviceMutationError =
	| DeleteSensoterraDevice400
	| void
	| DeleteSensoterraDevice404
	| DeleteSensoterraDevice500;
export type DeleteSensoterraDeviceMutationVariables = {
	sensoterraDeviceId: string;
};

/**
 * @summary Delete a Sensoterra device
 */
export const useDeleteSensoterraDevice = <
	TError =
		| DeleteSensoterraDevice400
		| void
		| DeleteSensoterraDevice404
		| DeleteSensoterraDevice500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof deleteSensoterraDevice>>,
			TError,
			DeleteSensoterraDeviceMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof deleteSensoterraDevice>>,
	TError,
	DeleteSensoterraDeviceMutationVariables,
	TContext
> => {
	return useMutation(
		getDeleteSensoterraDeviceMutationOptions(options),
		queryClient,
	);
};
